import React, { useMemo, useRef } from 'react';

import { Box } from '@opendoor/bricks-next';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { transformHeaderNavToMegaNav } from 'cms';
import Footer from 'components/landing-pages-v2/shared/footer/Footer';
import Header from 'components/landing-pages-v2/shared/header/Header';
import { NavMenuProvider } from 'components/landing-pages-v2/shared/NavMenuContext';
import GlobalStyles from 'components/shared/GlobalStyles';
import { ISiteFooter, ISiteHeader } from 'declarations/contentful';
import Head from 'next/head';

const setHeight = `
  html,
  body,
  div#__next {
    height: 100%;
  }
`;

const mainNavLinks = [
  {
    name: 'How it works',
    href: '/how-it-works-sell',
  },
  {
    name: 'Browse homes',
    href: '/homes',
  },
];

const menuPanelNavLinks = [
  {
    name: 'Work with a builder',
    href: '/builder',
  },
  {
    name: 'Opendoor for Agents',
    href: '/agents',
  },
];

interface MarketplaceMapLayoutProps {
  children: React.ReactNode;
  header?: ISiteHeader | null;
  footer?: ISiteFooter;
}

const MarketplaceMapLayout = ({ children, header, footer }: MarketplaceMapLayoutProps) => {
  const megaNavData = header?.fields?.mainNav
    ? transformHeaderNavToMegaNav(header.fields.mainNav)
    : undefined;

  const closeMenuRef = useRef(() => {});
  const navMenuValue = useMemo(() => ({ closeMenu: () => closeMenuRef.current() }), []);

  return (
    <ThemedLite
      baseTheme="novo"
      team="marketplace"
      launchDateString={new Date().toISOString()}
      overrides={{}}
    >
      <Head>
        <link rel="preconnect" href="https://images.opendoor.com" />
        <link rel="dns-prefetch" href="https://images.opendoor.com" />
        <link rel="preconnect" href="https://cdn-cosmos.opendoor.com" />
        <link rel="dns-prefetch" href="https://cdn-cosmos.opendoor.com" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://www.opendoor.com/apple-touch-icon-144.png"
        />
        <style>{setHeight}</style>
      </Head>
      <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
      <NavMenuProvider value={navMenuValue}>
        <Box display="flex" flexDirection="column" height="100%">
          {/* always 72px tall */}
          <Header
            slug="homes"
            mainNavLinks={mainNavLinks}
            menuPanelNavLinks={menuPanelNavLinks}
            megaNavData={megaNavData}
            closeMenuRef={closeMenuRef}
          />
          <Box
            tag="main"
            tabIndex={-1}
            display="flex"
            overflow="hidden"
            backgroundColor="$backgroundPrimary"
            height="calc(100% - 72px)"
          >
            {children}
          </Box>
          <Footer siteFooter={footer} />
        </Box>
      </NavMenuProvider>
    </ThemedLite>
  );
};

export default MarketplaceMapLayout;
